import Button from '@blocks/Button'
import Flex from '@blocks/Flex'
import Grid from '@blocks/Grid'
import NudityNotification from '@blocks/NudityNotification'
import { useConfigContext } from '@contexts/config'
import useSalon from '@general/models/Salon'
import { formatDate, formatJuryName, route } from '@helpers'
import useTitle from '@hooks/useTitle'
import { ReactComponent as ChevronIcon } from '@icons/chevron.svg'
import { Box, Container } from '@styles/helpers'
import { tablet } from '@styles/mixins'
import { grid } from '@styles/vars'
import dayjs from 'dayjs'
import React from 'react'
import { useParams } from 'react-router-dom'
import styled, { css } from 'styled-components'
import SalonConditionsContext, { useSalonConditionsContext } from './context'
import FiapPatronageImage from './images/fiap.jpg'
import PsaPatronageImage from './images/psa.jpg'
import RichEditor from '@components/modules/RichEditor/RichEditor'

export default function SalonConditionsPage() {    
    const { slug } = useParams<{ slug: string }>()
    const salon = useSalon(slug)

    const name = salon.state?.name || "Loading..."
    useTitle(`Conditions: ${name}`)

    if (!salon.state) 
        return null

    return (
        <SalonConditionsContext.Provider value={{ salon } as General.Contexts.SalonConditions}>
            <SalonConditionsContainer>
                <Grid>
                    <Grid gap="20px">
                        <Button type="tertiary" scheme="outline" to={route("general:salon", slug)} icon={<ChevronStyledIcon />}>Information</Button>
                        <Grid gap="10px">
                            <h1>Conditions</h1>
                            <h2 className="h3">{salon.state.name}</h2>
                        </Grid>
                    </Grid>
                    <Box narrow background="#fff">
                        <Rules />
                    </Box>
                </Grid>
            </SalonConditionsContainer>
        </SalonConditionsContext.Provider>
        
    )
}

// COMPONENTS
const Rules = () => {
    const { salon } = useSalonConditionsContext()
    const { support } = useConfigContext()
    
    return (
        <Grid gap="20px">
            <Flex gap="10px" flexWrap="wrap">
                {salon.attrs.organizations().array.map(item => <OrganizationLogo key={item.id}><img src={item.logo_url} /></OrganizationLogo>)}
            </Flex>
            <Grid columns="3" gap="20px">
                <div>
                    <h2 className="h3">{salon.state.name}</h2>
                    {salon.state.patronages.map(patronage => <p><b>{patronage.organization.name} {patronage.organization.label}</b><span>: {patronage.number}</span></p>)}
                </div>
                <div>
                    <h2 className="h3">Contacts</h2>
                    <ul>
                        <li><b>Exhibition Chairman</b><span>: Aleksey Anisimov (<a href={`mailto:${support}`}>{support}</a> or <a href={route("general:support")} target="_blank">support</a>)</span></li>
                        <li><b>Postal address</b><span>: Lesya Kurbasa 4, office 127, Kyiv, Ukraine</span></li>
                    </ul>
                </div>
                <div>
                    <h2 className="h3">Sections</h2>
                    <ul>
                        {salon.state.sections.map(section => <li key={section.id}>{section.name}</li>)}
                    </ul>
                </div>
                <div>
                    <h2 className="h3">Fees</h2>
                    <p><span>Entry fee is </span><b>€{salon.state.fee}</b><span>. Groups are eligible for discounts:</span></p> 
                    <ul>
                        <li>10 photographers — 30%</li>
                        <li>20 photographers — 40%</li>
                        <li>30 photographers — 50%</li>
                        <li>40+ photographers — 60%</li>
                    </ul>
                    {salon.state.early_bird && <p>Early Bird discount of {salon.state.early_bird.discount}% is available for all participants using discount code and with limitation in time: till {formatDate(salon.state.early_bird.until)}.</p>}
                    <p>Payment can be made on the website after submitting your application. To make payment you can use credit/debit cards or Pay Pal.</p>
                </div>
                <div>
                    <h2 className="h3">Calendar</h2>
                    <ul>
                        <li><b>Closing date</b><span>: {formatDate(salon.state.closing_date)}</span></li>
                        <li><b>Judging completed by</b><span>: {getPrevDay(salon.state.results_date)}</span></li>
                        <li><b>Notifications</b><span>: {formatDate(salon.state.results_date)}</span></li>
                        <li><b>Online gallery posted by</b><span>: {formatDate(salon.state.results_date)}</span></li>
                        <li><b>Awards mailed</b><span>: {formatDate(salon.state.awards_date)}</span></li>
                        <li><b>Catalogues posted by</b><span>: {formatDate(salon.state.awards_date)}</span></li>
                        <li><b>Public showing</b><span>: {getPublicShowingDateString(salon.state.awards_date)}</span></li>
                    </ul>
                </div>
                <div>
                    <h2 className="h3">Judges</h2>
                    <ul>
                        {salon.state.juries.map(jury => <li key={jury.id}>{formatJuryName(jury)}</li>)}
                    </ul>
                </div>
                <div>
                    <h2 className="h3">Awards</h2>
                    <ul>
                        <li>{salon.state.awards_count + 1} international awards from {salon.attrs.organizations().string} and International Salons</li>
                        <li>
                            {salon.attrs.organizations().has('FIAP') 
                                ? <span><b>Best Author Award</b><span>: FIAP Blue Pin</span></span> 
                                : <span><b>Best Author Award</b><span>: {salon.state.name} Best Author</span></span>}
                        </li>
                        {salon.state.awards_by_section.map(section => <li><b>{section.name}</b><span>: {section.awards.join(', ')}</span></li>)}
                        <li><b>Digital diplomas</b><span> are sent to all awarded participants together with awards.</span></li>
                        <li><b>Digital acceptance certificates</b><span> are sent to all accepted authors</span></li>
                    </ul>
                </div>
                <div>
                    <h2 className="h3">Catalogue</h2>
                    <p>PDF downloadable from web site by exhibitors which is called E-book. The link will be sent by email and it can be downloaded from your personal profile.</p>
                </div>
            </Grid>
            {salon.state.nudity_restricted && (
                <Grid gap="15px">
                    <h2 className="h3">Important</h2>
                    <NudityNotification />
                </Grid>
            )}
            <RichEditor value={salon.state.conditions} readOnly={true} />
            {salon.attrs.organizations().has('PSA') && (
                <PatronageLogos>
                    <div><img src={PsaPatronageImage} /></div>
                </PatronageLogos>
            )}
        </Grid>
    )
}

// STYLES
const OrganizationLogo = styled.div`
    img {
        max-height: 84px;
    }
`

const SalonConditionsContainer = styled(Container)`
    padding-top: ${grid.margins.phone};
    padding-bottom: ${grid.margins.phone};

    ${tablet(css`
        padding-top: ${grid.margins.tablet};
        padding-bottom: ${grid.margins.tablet};
    `)}
`

const ChevronStyledIcon = styled(ChevronIcon)`
    display: block;
    transform: rotate(90deg);
`

const PatronageLogos = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;

    & > div {
        width: 100%;
    }

    img {
        width: 100%;
    }

    & > div:not(:last-child) {
        margin-bottom: 20px;
    }

    ${tablet(css`
        flex-direction: row;

        & > div:not(:last-child) {
            margin-bottom: 0;
            margin-right: 60px;
        }
    `)}
`

function getNextDay(date: string) {
    return dayjs(date).add(1, 'day').format("MMMM D, YYYY")
}

function getPrevDay(date: string) {
    return dayjs(date).subtract(1, 'day').format("MMMM D, YYYY")
}

function getPublicShowingDateString(date: string) {
    const firstMonth = dayjs(date).add(1, 'month').format("MMMM YYYY")
    const lastMonth = dayjs(date).add(6, 'month').format("MMMM YYYY")
    return `${firstMonth} - ${lastMonth}`
}